import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@common-fe/common-fe';

import { api } from '@/api';
import { API_URL } from '@/dtos';

import { FileDtoWithMediaSource, FileStatus } from '../FileManager.constants';

import { FileDtoPayload, formatFile } from './useFilesList.query';
import { addBacklightingFields } from './useFilesUploadQuery';

const changeTemporarily = (data: FileDtoPayload) => {
  const changedData = { ...data };

  delete changedData.uploaded_by;
  delete changedData.partners_names;
  delete changedData.employers_names;

  return changedData;
};

const useFilesProcess = (allFiles: FileDtoWithMediaSource[], isReset?: boolean) => {
  const [processedFiles, setProcessedFiles] = useState<FileDtoWithMediaSource[]>([]);
  const [successfullyProcessedFile, setSuccessfullyProcessedFile] = useState<FileDtoWithMediaSource>();

  useEffect(() => {
    if (successfullyProcessedFile) {
      setProcessedFiles([
        ...processedFiles.filter((file) => file.id !== successfullyProcessedFile.id),
        successfullyProcessedFile,
      ] as FileDtoWithMediaSource[]);
      setSuccessfullyProcessedFile(undefined);
    }
  }, [successfullyProcessedFile, processedFiles]);

  useEffect(
    () => {
      if (isReset && processedFiles.length) {
        setProcessedFiles([]);
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [isReset]
  );

  const { mutateAsync, isSuccess, isLoading } = useMutation(
    (file: FileDtoWithMediaSource) =>
      api.patch<FileDtoPayload>(API_URL.FILE_APPROVE_BY_FILE_ID(file.id), undefined),
    {
      onSuccess: ({ data }) => {
        const changedPayload = changeTemporarily(data);
        const formattedFile = formatFile(changedPayload);

        setSuccessfullyProcessedFile({
          ...formattedFile,
          ...addBacklightingFields(formattedFile.status),
        } as FileDtoWithMediaSource);
      },
    }
  );

  const files = useMemo(
    () =>
      allFiles.map((file) => {
        if (file.status === FileStatus.Uploaded) {
          const processedFile = processedFiles.find((processFile) => processFile.id === file.id);

          return processedFile || file;
        }

        return file;
      }),
    [allFiles, processedFiles]
  );

  return {
    onFileProcess: mutateAsync,
    processedFiles: files,
    isSuccess,
    isLoading,
  };
};

export default useFilesProcess;
